import * as QRCode from 'qrcode'
import { useEffect, useMemo, useState } from 'react'

export interface QrCodeDisplayProps {
  value: string
  size?: number
  className?: string
  margin?: number
  foregroundColor?: string
  backgroundColor?: string
  errorCorrectionLevel?: QRCode.QRCodeErrorCorrectionLevel
  ariaLabel?: string
}

const sanitizeSvg = (svg: string) => {
  return svg.replace(/<\?xml[^>]*>/g, '').replace(/<!DOCTYPE[^>]*>/gi, '')
}

export function QrCodeDisplay({
  value,
  size = 240,
  className,
  margin = 0,
  foregroundColor = '#1f1f1f',
  backgroundColor = '#ffffff',
  errorCorrectionLevel = 'M',
  ariaLabel = 'QR code',
}: QrCodeDisplayProps) {
  const [svgMarkup, setSvgMarkup] = useState<string | null>(null)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let cancelled = false

    if (!value) {
      setSvgMarkup(null)
      setError(null)
      return
    }

    QRCode.toString(value, {
      type: 'svg',
      errorCorrectionLevel,
      margin,
      width: size,
      color: {
        dark: foregroundColor,
        light: backgroundColor,
      },
    })
      .then((svg) => {
        if (!cancelled) {
          setSvgMarkup(sanitizeSvg(svg))
          setError(null)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
          setSvgMarkup(null)
        }
      })

    return () => {
      cancelled = true
    }
  }, [backgroundColor, errorCorrectionLevel, foregroundColor, margin, size, value])

  const dataUrl = useMemo(() => {
    if (!svgMarkup) return null
    return `data:image/svg+xml;utf8,${encodeURIComponent(svgMarkup)}`
  }, [svgMarkup])

  const content = useMemo(() => {
    if (dataUrl) {
      return <img className="h-full w-full" src={dataUrl} alt={ariaLabel} draggable={false} />
    }

    if (error) {
      return (
        <div className="flex h-full w-full items-center justify-center text-xs text-[#ef4444]">
          QRコードを生成できませんでした
        </div>
      )
    }

    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="h-2/3 w-2/3 animate-pulse rounded-lg bg-[#e2e8f0]" />
      </div>
    )
  }, [ariaLabel, dataUrl, error])

  return (
    <div
      className={`relative overflow-hidden ${className ?? ''}`}
      style={{ width: size, height: size }}
    >
      {content}
    </div>
  )
}

export default QrCodeDisplay
